import React from 'react';
import BaseScadaNode from './BaseScadaNode';

export function ChillerSVG({ config = {}, width = 120, height = 80, liveValues = {} }) {
    const running = parseFloat(liveValues.state ?? 1) >= parseFloat(config.threshold ?? 0.5);
    const hasError = liveValues.error === 1 || liveValues.error === true ||
        liveValues.error === '1' || liveValues.error === 'true';
    const statusColor = hasError ? '#EF4444' : running ? '#22C55E' : '#64748B';

    const bodyX = 4, bodyY = 4;
    const bodyW = width - 8, bodyH = height - 8;

    // Port Y positions must match PORT_CONFIG offsets (30% and 70% of total node height)
    const portTopY = height * 0.30;
    const portBotY = height * 0.70;

    // Three vertical sections: evaporator | compressor | condenser
    const evapW = bodyW * 0.32;
    const condW = bodyW * 0.32;
    const evapX = bodyX;
    const condX = bodyX + bodyW - condW;
    const compCX = bodyX + bodyW / 2;
    const compCY = bodyY + bodyH / 2;
    const compR = Math.min(bodyH * 0.22, 11);

    const coilTop = bodyY + 6;
    const coilH = bodyH - 12;

    const tempBound = liveValues.temp_supply != null;
    const tempVal = parseFloat(liveValues.temp_supply ?? 0);

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {/* Cabinet body */}
            <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH} rx={4}
                fill="#1E293B" stroke={statusColor} strokeWidth={2} />

            {/* Section dividers */}
            <line x1={bodyX + evapW} y1={bodyY + 5} x2={bodyX + evapW} y2={bodyY + bodyH - 5}
                stroke="#334155" strokeWidth={1} />
            <line x1={condX} y1={bodyY + 5} x2={condX} y2={bodyY + bodyH - 5}
                stroke="#334155" strokeWidth={1} />

            {/* LEFT: Evaporator coils (cold / CHW circuit) */}
            {[0, 1, 2, 3].map(i => (
                <line key={`evap_${i}`}
                    x1={evapX + 4} y1={coilTop + i * (coilH / 3.5)}
                    x2={evapX + evapW - 4} y2={coilTop + i * (coilH / 3.5)}
                    stroke="#3B82F6" strokeWidth={1.5} opacity={running ? 0.9 : 0.3} />
            ))}

            {/* CENTER: Compressor */}
            <circle cx={compCX} cy={compCY} r={compR}
                fill="#0F172A" stroke={statusColor} strokeWidth={1.5} />
            {[0, 120, 240].map(deg => {
                const rad = deg * Math.PI / 180;
                return (
                    <line key={deg}
                        x1={compCX} y1={compCY}
                        x2={compCX + Math.cos(rad) * compR * 0.7}
                        y2={compCY + Math.sin(rad) * compR * 0.7}
                        stroke={statusColor} strokeWidth={1.5} strokeLinecap="round" />
                );
            })}
            <text x={compCX} y={compCY + 3} textAnchor="middle"
                fill={statusColor} fontSize={8} fontWeight="bold">C</text>

            {/* RIGHT: Condenser coils (warm / CW circuit) */}
            {[0, 1, 2, 3].map(i => (
                <line key={`cond_${i}`}
                    x1={condX + 4} y1={coilTop + i * (coilH / 3.5)}
                    x2={condX + condW - 4} y2={coilTop + i * (coilH / 3.5)}
                    stroke="#F59E0B" strokeWidth={1.5} opacity={running ? 0.9 : 0.3} />
            ))}

            {/*
             * PORT STUBS — colored and labeled:
             * Left side (blue) = CHW circuit (chilled water)
             *   top  30%: chw_out → supply to process (cold water OUT)
             *   bot  70%: chw_in  → return from process (warm water IN)
             * Right side (amber) = CW circuit (condenser water)
             *   top  30%: cw_in   → condenser water in
             *   bot  70%: cw_out  → purge / drain
             */}

            {/* chw_out: supply (left, top) — cold exits chiller */}
            <rect x={0} y={portTopY - 2} width={bodyX} height={4} rx={1} fill="#3B82F6" />
            <text x={evapX + 3} y={portTopY - 2} fill="#93C5FD" fontSize={6} fontWeight="bold"
                dominantBaseline="auto">Sup.</text>

            {/* chw_in: return (left, bottom) — warm returns from process */}
            <rect x={0} y={portBotY - 2} width={bodyX} height={4} rx={1} fill="#1D4ED8" />
            <text x={evapX + 3} y={portBotY + 9} fill="#93C5FD" fontSize={6} fontWeight="bold">Ret.</text>

            {/* cw_in: condenser water in (right, top) */}
            <rect x={bodyX + bodyW} y={portTopY - 2} width={4} height={4} rx={1} fill="#D97706" />
            <text x={condX + condW - 3} y={portTopY - 2} textAnchor="end" fill="#FCD34D"
                fontSize={6} fontWeight="bold" dominantBaseline="auto">CW↓</text>

            {/* cw_out: purge/drain (right, bottom) */}
            <rect x={bodyX + bodyW} y={portBotY - 2} width={4} height={4} rx={1} fill="#92400E" />
            <text x={condX + condW - 3} y={portBotY + 9} textAnchor="end" fill="#FCD34D"
                fontSize={6} fontWeight="bold">Prg.</text>

            {/* Status dot — top-right corner */}
            <circle cx={bodyX + bodyW - 6} cy={bodyY + 6} r={4}
                fill={statusColor} stroke="#0F172A" strokeWidth={1} />

            {/* Supply temperature */}
            {tempBound && (
                <text x={compCX} y={compCY + compR + 10}
                    textAnchor="middle" fill="#F1F5F9" fontSize={8} fontWeight="bold">
                    {isNaN(tempVal) ? '--' : tempVal.toFixed(1)}°C
                </text>
            )}

            {/* Error flash */}
            {hasError && (
                <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH} rx={4}
                    fill="none" stroke="#EF4444" strokeWidth={3}>
                    <animate attributeName="stroke-opacity" values="1;0;1" dur="0.6s" repeatCount="indefinite" />
                </rect>
            )}
        </svg>
    );
}

export default function ChillerNode(props) {
    const { data } = props;
    const w = data?.width || 120;
    const h = data?.height || 80;
    return (
        <BaseScadaNode {...props}>
            <ChillerSVG config={data?.config} width={w} height={h} liveValues={data?.liveValues || {}} />
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', marginTop: 2,
                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
